// Module du lot 3 (interface) : logistique, stations et hors connexion (E1 à E6, H3). Points d'intégration dans
// le routeur et l'aide contextuelle (architecture, section 13) : une ligne par module dans App.tsx et routes.ts.

import { createElement } from 'react';
import { PageChargements, PageConfirmerChargement } from './Chargements';
import { PageFicheOrdre, PageLivraisons, PageReception } from './Livraisons';
import { PageFlotte, PageMission, PageMissions } from './Missions';
import { PageCommandes, PagePlanification } from './Planification';
import { PageDossierRapprochement, PageRapprochements } from './Rapprochements';
import { PageAccueilStation, PageDeclarer, PageDemande, PageHistoriqueStation, PageHoraires, PageIncident } from './Station';
import { PageConflits, PageSynchronisation } from './Synchronisation';
import { PageVentes } from './Ventes';

export { PageAccueilStation } from './Station';
export { AccueilMissions } from './Missions';
export { BlocChargementsDuJour, BlocLivraisonsEnCours } from './Accueils';

const route = (chemin: string, composant: () => ReturnType<typeof createElement> | null) => ({ chemin, element: createElement(composant) });

export const inscription = {
  nom: 'logistique',
  routes: [
    route('/livraisons', PageLivraisons),
    route('/livraisons/:id', PageFicheOrdre),
    route('/livraisons/:id/reception', PageReception),
    route('/commandes', PageCommandes),
    route('/planification', PagePlanification),
    route('/chargements', PageChargements),
    route('/chargements/:id', PageConfirmerChargement),
    route('/rapprochements', PageRapprochements),
    route('/rapprochements/:id', PageDossierRapprochement),
    route('/missions', PageMissions),
    route('/missions/:id', PageMission),
    route('/flotte', PageFlotte),
    route('/ventes', PageVentes),
    route('/synchronisation', PageSynchronisation),
    route('/synchronisation/conflits', PageConflits),
    route('/station', PageAccueilStation),
    route('/declarer', PageDeclarer),
    route('/station/demande', PageDemande),
    route('/station/incident', PageIncident),
    route('/station/horaires', PageHoraires),
    route('/station/historique', PageHistoriqueStation),
  ],
  pages: [
    {
      motif: /^\/livraisons$/,
      libelle: 'Livraisons',
      parent: '/',
      aide: 'Missions en cours, exceptions d’abord : retard calculé contre l’arrivée prévue, arrêt prolongé sans étape, position ancienne ou inconnue. Un point de la carte est une dernière position connue, avec son âge et sa source (appareil ou déclarée). La station et l’établissement y confirment les livraisons attendues.',
    },
    {
      motif: /^\/livraisons\/[^/]+\/reception$/,
      libelle: 'Réception',
      parent: '/livraisons',
      aide: 'Parcours en huit étapes : référence, livraison retrouvée, quantités par compartiment, comparaison, différence, justificatif, enregistrement, mise à jour du stock à la validation. Au-delà de la tolérance (D-019), un dossier de rapprochement est ouvert. Une réception partielle laisse le reliquat en transit pendant le délai paramétré (D-020). La saisie fonctionne hors connexion.',
    },
    {
      motif: /^\/livraisons\/[^/]+$/,
      libelle: 'Ordre de transport',
      parent: '/livraisons',
      aide: 'Fiche de l’ordre : parties, véhicule et conducteur, compartiments et scellés, étapes avec position et heure de réception, réceptions avec écart et tolérance, bons de chargement et de livraison en PDF, canal de service de la livraison. L’annulation après chargement crée un mouvement de retour (R-20).',
    },
    {
      motif: /^\/commandes$/,
      libelle: 'Commandes',
      parent: '/',
      aide: 'Besoins exprimés par vos stations et les établissements : acceptation ou refus motivé, regroupement en commandes. Un besoin accepté se planifie depuis la page de planification.',
    },
    {
      motif: /^\/planification$/,
      libelle: 'Planification',
      parent: '/commandes',
      aide: 'Propositions d’affectation calculées par le serveur (dépôt où vous avez du disponible, véhicule libre, dates), jamais imposées. L’émission réserve le volume sur votre position au dépôt ; si le disponible est insuffisant, une dérogation accordée par le dépôt est requise (D-026).',
    },
    {
      motif: /^\/chargements$/,
      libelle: 'Chargements',
      parent: '/',
      aide: 'Ordres émis pour vos dépôts. La confirmation crée la sortie validée et la ligne de transit et lève la réservation. Une dérogation tracée permet de charger au-delà du disponible du propriétaire (D-026).',
    },
    {
      motif: /^\/chargements\/[^/]+$/,
      libelle: 'Confirmation du chargement',
      parent: '/chargements',
      aide: 'Bac de sortie, volume par compartiment, scellés, température et densité. En cas de disponible insuffisant, la dérogation est accordée par une personne habilitée avec un motif.',
    },
    {
      motif: /^\/rapprochements$/,
      libelle: 'Rapprochements',
      parent: '/',
      aide: 'Dossiers ouverts automatiquement au-delà de la tolérance, à l’expiration d’un reliquat ou sur contestation. Chaque partie apporte ses observations et hypothèses d’origine ; la clôture est motivée par une personne habilitée.',
    },
    {
      motif: /^\/rapprochements\/[^/]+$/,
      libelle: 'Dossier de rapprochement',
      parent: '/rapprochements',
      aide: 'Constat de l’écart, réceptions, observations des parties, justificatifs, historique et clôture (correction, justification technique, signalement aux services compétents). Le canal de service du dossier réunit les parties.',
    },
    {
      motif: /^\/missions$/,
      libelle: 'Missions',
      parent: '/',
      aide: 'Missions de votre organisation, à traiter d’abord : position ancienne, véhicule ou conducteur à affecter, retard. Hors connexion, les missions chargées sur l’appareil restent consultables.',
    },
    {
      motif: /^\/missions\/[^/]+$/,
      libelle: 'Mission',
      parent: '/missions',
      aide: 'Départ, étapes, arrêts, incident et arrivée, enregistrés sur l’appareil avec la position de l’appareil si vous l’autorisez, sinon le lieu déclaré. Chaque étape affiche son état jusqu’à l’accusé du serveur.',
    },
    { motif: /^\/flotte$/, libelle: 'Flotte', parent: '/missions', aide: 'Véhicules et conducteurs actifs de votre organisation, avec leur mission en cours.' },
    {
      motif: /^\/ventes$/,
      libelle: 'Ventes',
      parent: '/',
      aide: 'Bilan journalier par produit (volume et nombre de transactions agrégé), saisi ou importé en CSV ou XLSX. Une variation supérieure à trois fois la moyenne mobile est signalée ; une correction est motivée et tracée. Les ventes alimentent la consommation utilisée pour l’autonomie (D-027).',
    },
    {
      motif: /^\/synchronisation$/,
      libelle: 'Synchronisation',
      parent: '/',
      aide: 'File de cet appareil : enregistré sur cet appareil, en attente d’envoi, envoyé en attente d’accusé, synchronisé, conflit à résoudre, refusé avec son motif. L’envoi reprend à l’ouverture et au retour du réseau.',
    },
    {
      motif: /^\/synchronisation\/conflits$/,
      libelle: 'Conflits',
      parent: '/',
      aide: 'Saisies hors connexion d’un même objet par deux personnes. Les deux versions sont présentées ; le validateur en retient une avec un motif, inscrit au journal d’audit.',
    },
    { motif: /^\/station$/, libelle: 'Station', parent: '/', aide: 'Accueil de la station : exceptions d’abord, état de chaque produit, actions ouvertes à l’exploitant.' },
    {
      motif: /^\/declarer$/,
      libelle: 'Déclarer',
      parent: '/',
      aide: 'Relevé des cuves et statut de vente de chaque produit, en moins de deux minutes. Un stock positif avec une pompe hors service se déclare « non distribuable » : il n’est jamais compté comme disponible. La déclaration fonctionne hors connexion.',
    },
    { motif: /^\/station\/demande$/, libelle: 'Demande de réapprovisionnement', parent: '/', aide: 'Le besoin est adressé à votre distributeur, qui l’accepte ou le refuse avec un motif. La demande fonctionne hors connexion.' },
    { motif: /^\/station\/incident$/, libelle: 'Signaler un incident', parent: '/', aide: 'Pompe, rupture, fermeture, sécurité. Une rupture confirmée met le produit en rupture dans le statut de la station. Le signalement fonctionne hors connexion.' },
    { motif: /^\/station\/horaires$/, libelle: 'Horaires', parent: '/', aide: 'Horaires d’ouverture par jour, versionnés.' },
    { motif: /^\/station\/historique$/, libelle: 'Historique', parent: '/', aide: 'Déclarations, livraisons, demandes et incidents de la station.' },
  ],
} as const;
